import { useState, useEffect, useCallback } from 'react';
import {
  Truck, Search, Plus, Pencil, Delete, X, Save,
  ChevronLeft, ChevronRight, Phone, Mail, Users, FolderOpen, RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import { gastosApi, contabilidadApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import useToolbarResponsive from '../../hooks/useToolbarResponsive';

// Toolbar button component
function ToolbarButton({ icon: Icon, label, onClick, disabled, variant = 'default' }) {
  const baseClasses = "flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px]";
  const variants = {
    default: "border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400",
    primary: "border border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <Icon className="h-5 w-5 mb-1" />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

const tipoIdentificacionLabels = {
  '01': 'Física',
  '02': 'Jurídica',
  '03': 'DIMEX',
  '04': 'NITE',
  '05': 'Extranjero'
};

export default function Proveedores() {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';

  // Helper to check specific permissions
  const hasPermission = (modulo, accion) => {
    if (isAdmin) return true;
    return user?.permisos?.some(p => p.modulo === modulo && p.accion === accion);
  };

  // CxP Proveedores permissions
  const canCreate = hasPermission('cxp', 'crear');
  const canEdit = hasPermission('cxp', 'editar');
  const canDelete = hasPermission('cxp', 'eliminar');

  const [activeTab, setActiveTab] = useState('proveedores');
  const { searchRef, buttonsRef } = useToolbarResponsive([activeTab]);

  // Proveedores state
  const [proveedores, setProveedores] = useState([]);
  const [allProveedores, setAllProveedores] = useState([]); // For group assignment
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [showModal, setShowModal] = useState(false);
  const [editingProveedor, setEditingProveedor] = useState(null);

  // Grupos state
  const [grupos, setGrupos] = useState([]);
  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const [showGrupoModal, setShowGrupoModal] = useState(false);
  const [editingGrupo, setEditingGrupo] = useState(null);

  const fetchProveedores = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { q: searchTerm })
      };

      const response = await gastosApi.listProveedores(params);
      setProveedores(response.data.data || []);
      setPagination(prev => ({ ...prev, ...response.data.pagination }));
    } catch (error) {
      console.error('Error fetching proveedores:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm]);

  const fetchAllProveedores = async () => {
    try {
      const response = await gastosApi.listProveedores({ limit: 1000 });
      setAllProveedores(response.data.data || []);
    } catch (error) {
      console.error('Error fetching all proveedores:', error);
    }
  };

  const fetchGrupos = async () => {
    try {
      setLoadingGrupos(true);
      const response = await gastosApi.listGruposProveedores();
      setGrupos(response.data.data || []);
    } catch (error) {
      console.error('Error fetching grupos:', error);
    } finally {
      setLoadingGrupos(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'proveedores') {
      fetchProveedores();
    } else {
      fetchGrupos();
      fetchAllProveedores();
    }
  }, [activeTab, fetchProveedores]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchProveedores();
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleDelete = async (proveedor) => {
    if (!confirm(`¿Eliminar al proveedor ${proveedor.nombre}?`)) return;

    try {
      await gastosApi.deleteProveedor(proveedor.id);
      toast.success('Proveedor eliminado');
      fetchProveedores();
    } catch (error) {
      console.error('Error deleting proveedor:', error);
    }
  };

  const handleDeleteGrupo = async (grupo) => {
    if (!confirm(`¿Eliminar el grupo "${grupo.nombre}"?`)) return;

    try {
      await gastosApi.deleteGrupoProveedor(grupo.id);
      toast.success('Grupo eliminado');
      fetchGrupos();
    } catch (error) {
      console.error('Error deleting grupo:', error);
    }
  };

  const openEditModal = (proveedor) => {
    setEditingProveedor(proveedor);
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingProveedor(null);
    setShowModal(true);
  };

  const openEditGrupoModal = async (grupo) => {
    try {
      const response = await gastosApi.getGrupoProveedor(grupo.id);
      setEditingGrupo(response.data.data);
      setShowGrupoModal(true);
    } catch (error) {
      console.error('Error fetching grupo:', error);
    }
  };

  const openCreateGrupoModal = () => {
    setEditingGrupo(null);
    setShowGrupoModal(true);
  };

  return (
    <div className="flex flex-col gap-4 h-[var(--alto-contenido)] min-h-0">
      {/* 27/10/2026: la pantalla ocupa el alto útil y la tabla hace scroll adentro (antes se alargaba toda la página) */}
      {/* Toolbar — buscador absolute izq, botones centrados al toolbar (responsive con hook) */}
      <div className="bg-white rounded-lg border p-3 relative">
        {activeTab === 'proveedores' && (
          <form ref={searchRef} onSubmit={handleSearch} className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Buscar por nombre o identificación..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 w-52 text-sm"
              />
            </div>
            <span className="text-sm text-gray-500">{pagination.total} reg.</span>
          </form>
        )}
        <div ref={buttonsRef} className="flex items-center justify-center gap-2 flex-wrap">
          {activeTab === 'proveedores' && canCreate && (
            <ToolbarButton
              icon={Plus}
              label="Nuevo Proveedor"
              onClick={openCreateModal}
              variant="primary"
            />
          )}
          {activeTab === 'grupos' && isAdmin && (
            <ToolbarButton
              icon={Plus}
              label="Nuevo Grupo"
              onClick={openCreateGrupoModal}
              variant="primary"
            />
          )}
          <ToolbarButton
            icon={RefreshCw}
            label="Actualizar"
            onClick={() => activeTab === 'proveedores' ? fetchProveedores() : fetchGrupos()}
            disabled={loading || loadingGrupos}
          />
        </div>
      </div>

      {/* Tabs - Only show if admin */}
      {isAdmin && (
        <div className="border-b">
          <nav className="flex gap-4">
            <button
              onClick={() => setActiveTab('proveedores')}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium transition-colors ${
                activeTab === 'proveedores'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Truck size={18} />
              Proveedores
            </button>
            <button
              onClick={() => setActiveTab('grupos')}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium transition-colors ${
                activeTab === 'grupos'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <FolderOpen size={18} />
              Grupos de Proveedores
            </button>
          </nav>
        </div>
      )}

      {/* Proveedores Tab */}
      {activeTab === 'proveedores' && (
        <>

          {/* Table */}
          <div className="bg-white rounded-lg border overflow-hidden flex-1 min-h-0 flex flex-col">
            <div className="overflow-auto flex-1 min-h-0">
              <table className="w-full">
                <thead className="bg-gray-50 border-b sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Proveedor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Identificación</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Contacto</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                        Cargando...
                      </td>
                    </tr>
                  ) : proveedores.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                        No hay proveedores registrados
                      </td>
                    </tr>
                  ) : (
                    proveedores.map((proveedor) => (
                      <tr key={proveedor.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-gray-100 rounded-lg">
                              <Truck className="text-gray-600" size={20} />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{proveedor.nombre}</div>
                              {proveedor.nombreComercial && (
                                <div className="text-sm text-gray-500">{proveedor.nombreComercial}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-gray-900">{proveedor.identificacion}</div>
                          <div className="text-xs text-gray-500">
                            {tipoIdentificacionLabels[proveedor.tipoIdentificacion]}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {proveedor.email && (
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <Mail size={14} />
                              {proveedor.email}
                            </div>
                          )}
                          {proveedor.telefonoNumero && (
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <Phone size={14} />
                              {proveedor.telefonoCodigo && `+${proveedor.telefonoCodigo} `}
                              {proveedor.telefonoNumero}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-1">
                            {canEdit && (
                              <button
                                onClick={() => openEditModal(proveedor)}
                                className="p-1.5 hover:bg-blue-50 rounded"
                                title="Editar"
                              >
                                <Pencil className="h-5 w-5 text-blue-500" />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => handleDelete(proveedor)}
                                className="p-1.5 hover:bg-red-50 rounded"
                                title="Eliminar"
                              >
                                <Delete className="h-5 w-5 text-red-500" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <div className="text-sm text-gray-500">
                  Mostrando {((pagination.page - 1) * pagination.limit) + 1} a{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="p-2 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-sm text-gray-700">
                    Página {pagination.page} de {pagination.pages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                    className="p-2 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Grupos Tab - Admin only */}
      {isAdmin && activeTab === 'grupos' && (
        <div className="bg-white rounded-lg border overflow-hidden flex-1 min-h-0 flex flex-col">
          <div className="overflow-auto flex-1 min-h-0">
            <table className="w-full">
              <thead className="bg-gray-50 border-b sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Grupo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Descripción</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Proveedores</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loadingGrupos ? (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                      Cargando...
                    </td>
                  </tr>
                ) : grupos.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                      No hay grupos creados. Crea un grupo para organizar tus proveedores.
                    </td>
                  </tr>
                ) : (
                  grupos.map((grupo) => (
                    <tr key={grupo.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: grupo.color || '#6B7280' }}
                          />
                          <span className="font-medium text-gray-900">{grupo.nombre}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {grupo.descripcion || '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-sm">
                          <Users size={14} />
                          {grupo.miembrosCount}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => openEditGrupoModal(grupo)}
                            className="p-1.5 hover:bg-blue-50 rounded"
                            title="Editar"
                          >
                            <Pencil className="h-5 w-5 text-blue-500" />
                          </button>
                          <button
                            onClick={() => handleDeleteGrupo(grupo)}
                            className="p-1.5 hover:bg-red-50 rounded"
                            title="Eliminar"
                          >
                            <Delete className="h-5 w-5 text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Proveedor Modal */}
      {showModal && (
        <ProveedorModal
          proveedor={editingProveedor}
          onClose={() => {
            setShowModal(false);
            setEditingProveedor(null);
          }}
          onSuccess={() => {
            setShowModal(false);
            setEditingProveedor(null);
            fetchProveedores();
          }}
        />
      )}

      {/* Create/Edit Grupo Modal */}
      {showGrupoModal && (
        <GrupoModal
          grupo={editingGrupo}
          proveedores={allProveedores}
          onClose={() => {
            setShowGrupoModal(false);
            setEditingGrupo(null);
          }}
          onSuccess={() => {
            setShowGrupoModal(false);
            setEditingGrupo(null);
            fetchGrupos();
          }}
        />
      )}
    </div>
  );
}

// Provincias de Costa Rica
const PROVINCIAS = [
  { value: '1', label: 'San José' },
  { value: '2', label: 'Alajuela' },
  { value: '3', label: 'Cartago' },
  { value: '4', label: 'Heredia' },
  { value: '5', label: 'Guanacaste' },
  { value: '6', label: 'Puntarenas' },
  { value: '7', label: 'Limón' },
];

// Cantones por provincia
const CANTONES = {
  '1': [
    { value: '01', label: 'San José' },
    { value: '02', label: 'Escazú' },
    { value: '03', label: 'Desamparados' },
    { value: '04', label: 'Puriscal' },
    { value: '05', label: 'Tarrazú' },
    { value: '06', label: 'Aserrí' },
    { value: '07', label: 'Mora' },
    { value: '08', label: 'Goicoechea' },
    { value: '09', label: 'Santa Ana' },
    { value: '10', label: 'Alajuelita' },
    { value: '11', label: 'Vázquez de Coronado' },
    { value: '12', label: 'Acosta' },
    { value: '13', label: 'Tibás' },
    { value: '14', label: 'Moravia' },
    { value: '15', label: 'Montes de Oca' },
    { value: '16', label: 'Turrubares' },
    { value: '17', label: 'Dota' },
    { value: '18', label: 'Curridabat' },
    { value: '19', label: 'Pérez Zeledón' },
    { value: '20', label: 'León Cortés' },
  ],
  '2': [
    { value: '01', label: 'Alajuela' },
    { value: '02', label: 'San Ramón' },
    { value: '03', label: 'Grecia' },
    { value: '04', label: 'San Mateo' },
    { value: '05', label: 'Atenas' },
    { value: '06', label: 'Naranjo' },
    { value: '07', label: 'Palmares' },
    { value: '08', label: 'Poás' },
    { value: '09', label: 'Orotina' },
    { value: '10', label: 'San Carlos' },
    { value: '11', label: 'Zarcero' },
    { value: '12', label: 'Sarchí' },
    { value: '13', label: 'Upala' },
    { value: '14', label: 'Los Chiles' },
    { value: '15', label: 'Guatuso' },
    { value: '16', label: 'Río Cuarto' },
  ],
  '3': [
    { value: '01', label: 'Cartago' },
    { value: '02', label: 'Paraíso' },
    { value: '03', label: 'La Unión' },
    { value: '04', label: 'Jiménez' },
    { value: '05', label: 'Turrialba' },
    { value: '06', label: 'Alvarado' },
    { value: '07', label: 'Oreamuno' },
    { value: '08', label: 'El Guarco' },
  ],
  '4': [
    { value: '01', label: 'Heredia' },
    { value: '02', label: 'Barva' },
    { value: '03', label: 'Santo Domingo' },
    { value: '04', label: 'Santa Bárbara' },
    { value: '05', label: 'San Rafael' },
    { value: '06', label: 'San Isidro' },
    { value: '07', label: 'Belén' },
    { value: '08', label: 'Flores' },
    { value: '09', label: 'San Pablo' },
    { value: '10', label: 'Sarapiquí' },
  ],
  '5': [
    { value: '01', label: 'Liberia' },
    { value: '02', label: 'Nicoya' },
    { value: '03', label: 'Santa Cruz' },
    { value: '04', label: 'Bagaces' },
    { value: '05', label: 'Carrillo' },
    { value: '06', label: 'Cañas' },
    { value: '07', label: 'Abangares' },
    { value: '08', label: 'Tilarán' },
    { value: '09', label: 'Nandayure' },
    { value: '10', label: 'La Cruz' },
    { value: '11', label: 'Hojancha' },
  ],
  '6': [
    { value: '01', label: 'Puntarenas' },
    { value: '02', label: 'Esparza' },
    { value: '03', label: 'Buenos Aires' },
    { value: '04', label: 'Montes de Oro' },
    { value: '05', label: 'Osa' },
    { value: '06', label: 'Quepos' },
    { value: '07', label: 'Golfito' },
    { value: '08', label: 'Coto Brus' },
    { value: '09', label: 'Parrita' },
    { value: '10', label: 'Corredores' },
    { value: '11', label: 'Garabito' },
  ],
  '7': [
    { value: '01', label: 'Limón' },
    { value: '02', label: 'Pococí' },
    { value: '03', label: 'Siquirres' },
    { value: '04', label: 'Talamanca' },
    { value: '05', label: 'Matina' },
    { value: '06', label: 'Guácimo' },
  ],
};

// Distritos por provincia-canton
const DISTRITOS = {
  '1-01': [
    { value: '01', label: 'Carmen' },
    { value: '02', label: 'Merced' },
    { value: '03', label: 'Hospital' },
    { value: '04', label: 'Catedral' },
    { value: '05', label: 'Zapote' },
    { value: '06', label: 'San Francisco de Dos Ríos' },
    { value: '07', label: 'Uruca' },
    { value: '08', label: 'Mata Redonda' },
    { value: '09', label: 'Pavas' },
    { value: '10', label: 'Hatillo' },
    { value: '11', label: 'San Sebastián' },
  ],
  '1-02': [
    { value: '01', label: 'Escazú' },
    { value: '02', label: 'San Antonio' },
    { value: '03', label: 'San Rafael' },
  ],
  '1-03': [
    { value: '01', label: 'Desamparados' },
    { value: '02', label: 'San Miguel' },
    { value: '03', label: 'San Juan de Dios' },
    { value: '04', label: 'San Rafael Arriba' },
    { value: '05', label: 'San Antonio' },
    { value: '06', label: 'Frailes' },
    { value: '07', label: 'Patarrá' },
    { value: '08', label: 'San Cristóbal' },
    { value: '09', label: 'Rosario' },
    { value: '10', label: 'Damas' },
    { value: '11', label: 'San Rafael Abajo' },
    { value: '12', label: 'Gravilias' },
    { value: '13', label: 'Los Guido' },
  ],
  '1-08': [
    { value: '01', label: 'Guadalupe' },
    { value: '02', label: 'San Francisco' },
    { value: '03', label: 'Calle Blancos' },
    { value: '04', label: 'Mata de Plátano' },
    { value: '05', label: 'Ipís' },
    { value: '06', label: 'Rancho Redondo' },
    { value: '07', label: 'Purral' },
  ],
  '1-09': [
    { value: '01', label: 'Santa Ana' },
    { value: '02', label: 'Salitral' },
    { value: '03', label: 'Pozos' },
    { value: '04', label: 'Uruca' },
    { value: '05', label: 'Piedades' },
    { value: '06', label: 'Brasil' },
  ],
  '1-13': [
    { value: '01', label: 'San Juan' },
    { value: '02', label: 'Cinco Esquinas' },
    { value: '03', label: 'Anselmo Llorente' },
    { value: '04', label: 'León XIII' },
    { value: '05', label: 'Colima' },
  ],
  '1-14': [
    { value: '01', label: 'San Vicente' },
    { value: '02', label: 'San Jerónimo' },
    { value: '03', label: 'La Trinidad' },
  ],
  '1-15': [
    { value: '01', label: 'San Pedro' },
    { value: '02', label: 'Sabanilla' },
    { value: '03', label: 'Mercedes' },
    { value: '04', label: 'San Rafael' },
  ],
  '1-18': [
    { value: '01', label: 'Curridabat' },
    { value: '02', label: 'Granadilla' },
    { value: '03', label: 'Sánchez' },
    { value: '04', label: 'Tirrases' },
  ],
  '2-01': [
    { value: '01', label: 'Alajuela' },
    { value: '02', label: 'San José' },
    { value: '03', label: 'Carrizal' },
    { value: '04', label: 'San Antonio' },
    { value: '05', label: 'Guácima' },
    { value: '06', label: 'San Isidro' },
    { value: '07', label: 'Sabanilla' },
    { value: '08', label: 'San Rafael' },
    { value: '09', label: 'Río Segundo' },
    { value: '10', label: 'Desamparados' },
    { value: '11', label: 'Turrúcares' },
    { value: '12', label: 'Tambor' },
    { value: '13', label: 'Garita' },
    { value: '14', label: 'Sarapiquí' },
  ],
  '2-10': [
    { value: '01', label: 'Quesada' },
    { value: '02', label: 'Florencia' },
    { value: '03', label: 'Buenavista' },
    { value: '04', label: 'Aguas Zarcas' },
    { value: '05', label: 'Venecia' },
    { value: '06', label: 'Pital' },
    { value: '07', label: 'La Fortuna' },
    { value: '08', label: 'La Tigra' },
    { value: '09', label: 'La Palmera' },
    { value: '10', label: 'Venado' },
    { value: '11', label: 'Cutris' },
    { value: '12', label: 'Monterrey' },
    { value: '13', label: 'Pocosol' },
  ],
  '3-01': [
    { value: '01', label: 'Oriental' },
    { value: '02', label: 'Occidental' },
    { value: '03', label: 'Carmen' },
    { value: '04', label: 'San Nicolás' },
    { value: '05', label: 'Aguacaliente' },
    { value: '06', label: 'Guadalupe' },
    { value: '07', label: 'Corralillo' },
    { value: '08', label: 'Tierra Blanca' },
    { value: '09', label: 'Dulce Nombre' },
    { value: '10', label: 'Llano Grande' },
    { value: '11', label: 'Quebradilla' },
  ],
  '3-03': [
    { value: '01', label: 'Tres Ríos' },
    { value: '02', label: 'San Diego' },
    { value: '03', label: 'San Juan' },
    { value: '04', label: 'San Rafael' },
    { value: '05', label: 'Concepción' },
    { value: '06', label: 'Dulce Nombre' },
    { value: '07', label: 'San Ramón' },
    { value: '08', label: 'Río Azul' },
  ],
  '4-01': [
    { value: '01', label: 'Heredia' },
    { value: '02', label: 'Mercedes' },
    { value: '03', label: 'San Francisco' },
    { value: '04', label: 'Ulloa' },
    { value: '05', label: 'Varablanca' },
  ],
  '4-03': [
    { value: '01', label: 'Santo Domingo' },
    { value: '02', label: 'San Vicente' },
    { value: '03', label: 'San Miguel' },
    { value: '04', label: 'Paracito' },
    { value: '05', label: 'Santo Tomás' },
    { value: '06', label: 'Santa Rosa' },
    { value: '07', label: 'Tures' },
    { value: '08', label: 'Pará' },
  ],
  '4-07': [
    { value: '01', label: 'San Antonio' },
    { value: '02', label: 'La Ribera' },
    { value: '03', label: 'La Asunción' },
  ],
  '5-01': [
    { value: '01', label: 'Liberia' },
    { value: '02', label: 'Cañas Dulces' },
    { value: '03', label: 'Mayorga' },
    { value: '04', label: 'Nacascolo' },
    { value: '05', label: 'Curubandé' },
  ],
  '5-03': [
    { value: '01', label: 'Santa Cruz' },
    { value: '02', label: 'Bolsón' },
    { value: '03', label: 'Veintisiete de Abril' },
    { value: '04', label: 'Tempate' },
    { value: '05', label: 'Cartagena' },
    { value: '06', label: 'Cuajiniquil' },
    { value: '07', label: 'Diriá' },
    { value: '08', label: 'Cabo Velas' },
    { value: '09', label: 'Tamarindo' },
  ],
  '6-01': [
    { value: '01', label: 'Puntarenas' },
    { value: '02', label: 'Pitahaya' },
    { value: '03', label: 'Chomes' },
    { value: '04', label: 'Lepanto' },
    { value: '05', label: 'Paquera' },
    { value: '06', label: 'Manzanillo' },
    { value: '07', label: 'Guacimal' },
    { value: '08', label: 'Barranca' },
    { value: '09', label: 'Monte Verde' },
    { value: '10', label: 'Isla del Coco' },
    { value: '11', label: 'Cóbano' },
    { value: '12', label: 'Chacarita' },
    { value: '13', label: 'Chira' },
    { value: '14', label: 'Acapulco' },
    { value: '15', label: 'El Roble' },
    { value: '16', label: 'Arancibia' },
  ],
  '6-06': [
    { value: '01', label: 'Quepos' },
    { value: '02', label: 'Savegre' },
    { value: '03', label: 'Naranjito' },
  ],
  '6-11': [
    { value: '01', label: 'Jacó' },
    { value: '02', label: 'Tárcoles' },
  ],
  '7-01': [
    { value: '01', label: 'Limón' },
    { value: '02', label: 'Valle La Estrella' },
    { value: '03', label: 'Río Blanco' },
    { value: '04', label: 'Matama' },
  ],
  '7-02': [
    { value: '01', label: 'Guápiles' },
    { value: '02', label: 'Jiménez' },
    { value: '03', label: 'Rita' },
    { value: '04', label: 'Roxana' },
    { value: '05', label: 'Cariari' },
    { value: '06', label: 'Colorado' },
    { value: '07', label: 'La Colonia' },
  ],
};

// Lista de países
const PAISES = [
  { codigo: 'CRI', nombre: 'Costa Rica' },
  { codigo: 'USA', nombre: 'Estados Unidos' },
  { codigo: 'MEX', nombre: 'México' },
  { codigo: 'GTM', nombre: 'Guatemala' },
  { codigo: 'SLV', nombre: 'El Salvador' },
  { codigo: 'HND', nombre: 'Honduras' },
  { codigo: 'NIC', nombre: 'Nicaragua' },
  { codigo: 'PAN', nombre: 'Panamá' },
  { codigo: 'COL', nombre: 'Colombia' },
  { codigo: 'VEN', nombre: 'Venezuela' },
  { codigo: 'ECU', nombre: 'Ecuador' },
  { codigo: 'PER', nombre: 'Perú' },
  { codigo: 'BOL', nombre: 'Bolivia' },
  { codigo: 'CHL', nombre: 'Chile' },
  { codigo: 'ARG', nombre: 'Argentina' },
  { codigo: 'URY', nombre: 'Uruguay' },
  { codigo: 'PRY', nombre: 'Paraguay' },
  { codigo: 'BRA', nombre: 'Brasil' },
  { codigo: 'ESP', nombre: 'España' },
  { codigo: 'DEU', nombre: 'Alemania' },
  { codigo: 'FRA', nombre: 'Francia' },
  { codigo: 'ITA', nombre: 'Italia' },
  { codigo: 'GBR', nombre: 'Reino Unido' },
  { codigo: 'CAN', nombre: 'Canadá' },
  { codigo: 'CHN', nombre: 'China' },
  { codigo: 'JPN', nombre: 'Japón' },
  { codigo: 'KOR', nombre: 'Corea del Sur' },
  { codigo: 'IND', nombre: 'India' },
  { codigo: 'AUS', nombre: 'Australia' },
  { codigo: 'OTR', nombre: 'Otro' }
];

// Proveedor Modal Component - Mejorado
function ProveedorModal({ proveedor, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    tipoIdentificacion: proveedor?.tipoIdentificacion || '02',
    identificacion: proveedor?.identificacion || '',
    nombre: proveedor?.nombre || '',
    nombreComercial: proveedor?.nombreComercial || '',
    email: proveedor?.email || '',
    telefonoCodigo: proveedor?.telefonoCodigo || '506',
    telefonoNumero: proveedor?.telefonoNumero || '',
    pais: proveedor?.pais || 'CRI',
    provincia: proveedor?.provincia || '',
    canton: proveedor?.canton || '',
    distrito: proveedor?.distrito || '',
    otrasSenas: proveedor?.otrasSenas || '',
    notas: proveedor?.notas || '',
    cuentaComprasCodigo: proveedor?.cuentaComprasCodigo || '',
    ibanCrc: proveedor?.ibanCrc || '',
    ibanUsd: proveedor?.ibanUsd || ''
  });
  // Cuentas de costo y gasto del plan de la empresa (para la cuenta de compras del proveedor)
  const [cuentasCompras, setCuentasCompras] = useState([]);
  useEffect(() => {
    contabilidadApi.getCuentas({ flat: true, soloMovimiento: true })
      .then(r => {
        const lista = r.data?.data || r.data || [];
        setCuentasCompras((Array.isArray(lista) ? lista : []).filter(c => ['gasto', 'costo'].includes(c.tipo)));
      })
      .catch(() => setCuentasCompras([]));
  }, []);
  const [loading, setLoading] = useState(false);
  const [consultando, setConsultando] = useState(false);
  const [consultado, setConsultado] = useState(false);
  const isEditing = !!proveedor;

  // Auto-consultar Hacienda cuando se completa la identificación (solo CR, solo crear)
  useEffect(() => {
    if (isEditing || consultado || consultando) return;
    const tipo = formData.tipoIdentificacion;
    if (tipo === '05') return; // No consultar extranjeros

    const digits = formData.identificacion.replace(/\D/g, '');
    const requiredLength = tipo === '01' ? 9 : tipo === '02' ? 10 : tipo === '03' ? 11 : tipo === '04' ? 10 : 0;
    if (!requiredLength || digits.length !== requiredLength) return;

    const timer = setTimeout(async () => {
      setConsultando(true);
      try {
        const res = await import('../../utils/api').then(m => m.emisoresApi.consultarHacienda(digits));
        if (res.data?.success && res.data?.data?.nombre) {
          setFormData(prev => ({ ...prev, nombre: res.data.data.nombre }));
          toast.success(`Proveedor encontrado: ${res.data.data.nombre}`);
          setConsultado(true);
        }
      } catch (e) {
        // Silent fail
      } finally {
        setConsultando(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.identificacion, formData.tipoIdentificacion, consultado, consultando, isEditing]);

  // Reset consultado when identificacion changes
  useEffect(() => {
    setConsultado(false);
  }, [formData.tipoIdentificacion]);

  // Auto-ajustar país cuando cambia tipo de identificación
  const handleTipoChange = (tipo) => {
    const updates = { tipoIdentificacion: tipo };
    if (tipo === '05') {
      // Extranjero: limpiar país CRI si estaba seleccionado
      if (formData.pais === 'CRI') {
        updates.pais = 'USA';
      }
    } else {
      // Nacional: forzar Costa Rica
      updates.pais = 'CRI';
    }
    setFormData({ ...formData, ...updates });
  };

  const esExtranjero = formData.tipoIdentificacion === '05';

  // Computed cantones y distritos
  const cantones = formData.provincia ? (CANTONES[formData.provincia] || []) : [];
  const distritos = formData.provincia && formData.canton
    ? (DISTRITOS[`${formData.provincia}-${formData.canton}`] || [])
    : [];

  const handleProvinciaChange = (provincia) => {
    setFormData({ ...formData, provincia, canton: '', distrito: '' });
  };

  const handleCantonChange = (canton) => {
    setFormData({ ...formData, canton, distrito: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.identificacion || !formData.nombre) {
      toast.error('Identificación y nombre son requeridos');
      return;
    }

    if (esExtranjero && (!formData.otrasSenas || formData.otrasSenas.length < 5)) {
      toast.error('Para proveedores extranjeros se requiere dirección (mín. 5 caracteres)');
      return;
    }

    try {
      setLoading(true);

      if (proveedor) {
        await gastosApi.updateProveedor(proveedor.id, formData);
        toast.success('Proveedor actualizado');
      } else {
        await gastosApi.createProveedor(formData);
        toast.success('Proveedor creado');
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving proveedor:', error);
      toast.error(error.response?.data?.error || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Truck className="text-blue-600" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {proveedor ? 'Editar Proveedor' : 'Nuevo Proveedor'}
              </h3>
              <p className="text-xs text-gray-500">
                {esExtranjero ? 'Proveedor extranjero no domiciliado' : 'Proveedor nacional'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Body - Layout horizontal */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Columna Izquierda */}
            <div className="space-y-3">
              {/* Identificación */}
              <div className="bg-gray-50 rounded-lg p-3 border">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Identificación</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Tipo *</label>
                    <select
                      value={formData.tipoIdentificacion}
                      onChange={(e) => handleTipoChange(e.target.value)}
                      className="w-full border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
                      disabled={!!proveedor}
                    >
                      <option value="01">Física</option>
                      <option value="02">Jurídica</option>
                      <option value="03">DIMEX</option>
                      <option value="04">NITE</option>
                      <option value="05">Extranjero</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">N. de Identificación *</label>
                    <div className="relative">
                      <input
                        type="text"
                        value={formData.identificacion}
                        onChange={(e) => setFormData({ ...formData, identificacion: e.target.value })}
                        placeholder={esExtranjero ? 'ID' : '3-101-123456'}
                        className="w-full border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      {consultando && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2">
                          <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">País</label>
                    <select
                      value={formData.pais}
                      onChange={(e) => setFormData({ ...formData, pais: e.target.value })}
                      className="w-full border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
                      disabled={!esExtranjero}
                    >
                      {PAISES.map(p => (
                        <option key={p.codigo} value={p.codigo}>{p.nombre}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Nombres */}
              <div className="bg-gray-50 rounded-lg p-3 border">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Datos</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Nombre / Razón Social *</label>
                    <input
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      placeholder="Nombre completo o razón social"
                      className="w-full border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Nombre Comercial</label>
                    <input
                      type="text"
                      value={formData.nombreComercial}
                      onChange={(e) => setFormData({ ...formData, nombreComercial: e.target.value })}
                      placeholder="Opcional"
                      className="w-full border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Cuenta de compras: a dónde va el gasto de sus facturas y FEC */}
              <div className="bg-gray-50 rounded-lg p-3 border">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Contabilidad</h4>
                <label className="block text-xs font-medium text-gray-700 mb-1">Cuenta de compras</label>
                <select
                  value={formData.cuentaComprasCodigo}
                  onChange={(e) => setFormData({ ...formData, cuentaComprasCodigo: e.target.value })}
                  className="w-full border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Automática (bienes → costo, servicios → gasto)</option>
                  {cuentasCompras.map(c => (
                    <option key={c.codigo} value={c.codigo}>{c.codigo} · {c.nombre}</option>
                  ))}
                </select>
              </div>

              {/* Cuentas IBAN para el archivo de transferencias del lote de pago (S7) */}
              <div className="bg-gray-50 rounded-lg p-3 border">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Cuentas para transferencias</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block text-xs font-medium text-gray-700">IBAN en colones
                    <input name="ibanCrc" value={formData.ibanCrc} onChange={(e) => setFormData({ ...formData, ibanCrc: e.target.value })} placeholder="CR00 0000 0000 0000 0000 00" className="mt-1 w-full border rounded-lg px-2 py-1.5 text-[12.5px] tracking-tight font-mono focus:ring-2 focus:ring-blue-500" />
                  </label>
                  <label className="block text-xs font-medium text-gray-700">IBAN en dólares
                    <input name="ibanUsd" value={formData.ibanUsd} onChange={(e) => setFormData({ ...formData, ibanUsd: e.target.value })} placeholder="CR00 0000 0000 0000 0000 00" className="mt-1 w-full border rounded-lg px-2 py-1.5 text-[12.5px] tracking-tight font-mono focus:ring-2 focus:ring-blue-500" />
                  </label>
                </div>
                <p className="text-[11px] text-gray-500 mt-2">Se usan en el archivo del banco de los lotes de pago. Se revisa el dígito de control.</p>
              </div>
            </div>

            {/* Columna Derecha */}
            <div className="space-y-3">
              {/* Contacto */}
              <div className="bg-gray-50 rounded-lg p-3 border">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Contacto</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="correo@ejemplo.com"
                        className="w-full border rounded-lg pl-8 pr-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Teléfono</label>
                    <div className="flex gap-1">
                      <input
                        type="text"
                        value={formData.telefonoCodigo}
                        onChange={(e) => setFormData({ ...formData, telefonoCodigo: e.target.value })}
                        className="w-14 shrink-0 border rounded-lg px-2 py-1.5 text-sm text-center focus:ring-2 focus:ring-blue-500"
                        placeholder="506"
                      />
                      <input
                        type="text"
                        value={formData.telefonoNumero}
                        onChange={(e) => setFormData({ ...formData, telefonoNumero: e.target.value })}
                        className="flex-1 min-w-0 border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
                        placeholder="8888-8888"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Dirección */}
              <div className="bg-gray-50 rounded-lg p-3 border">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">
                  Dirección {esExtranjero && <span className="text-red-500">*</span>}
                </h4>

                {/* Ubicación CR - solo si NO es extranjero */}
                {!esExtranjero && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Provincia</label>
                      <select
                        value={formData.provincia}
                        onChange={(e) => handleProvinciaChange(e.target.value)}
                        className="w-full border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Seleccionar...</option>
                        {PROVINCIAS.map(p => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Cantón</label>
                      <select
                        value={formData.canton}
                        onChange={(e) => handleCantonChange(e.target.value)}
                        disabled={!formData.provincia}
                        className="w-full border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      >
                        <option value="">Seleccionar...</option>
                        {cantones.map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Distrito</label>
                      <select
                        value={formData.distrito}
                        onChange={(e) => setFormData({ ...formData, distrito: e.target.value })}
                        disabled={!formData.canton || distritos.length === 0}
                        className="w-full border rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      >
                        <option value="">Seleccionar...</option>
                        {distritos.map(d => (
                          <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {esExtranjero ? 'Dirección Completa' : 'Otras Señas'}
                    {esExtranjero && <span className="text-red-500"> *</span>}
                  </label>
                  <textarea
                    value={formData.otrasSenas}
                    onChange={(e) => setFormData({ ...formData, otrasSenas: e.target.value })}
                    placeholder={esExtranjero ? 'Requerido para extranjeros (mín. 5 caracteres)' : 'Dirección exacta...'}
                    rows={2}
                    className="w-full border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
                    required={esExtranjero}
                    minLength={esExtranjero ? 5 : 0}
                  />
                </div>
              </div>

              {/* Notas */}
              <div className="bg-gray-50 rounded-lg p-3 border">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Notas Internas</h4>
                <textarea
                  value={formData.notas}
                  onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                  placeholder="Notas internas (no se envían a Hacienda)"
                  rows={2}
                  className="w-full border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-3 border-t bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2 border-2 border-green-500 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50 transition-colors font-medium"
          >
            {loading ? (
              <>
                <span className="animate-spin h-4 w-4 border-2 border-green-500 border-t-transparent rounded-full"></span>
                Guardando...
              </>
            ) : (
              <>
                <Save size={18} />
                Guardar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Grupo Modal Component (Outline style)
function GrupoModal({ grupo, proveedores, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    nombre: grupo?.nombre || '',
    descripcion: grupo?.descripcion || '',
    color: grupo?.color || '#6B7280'
  });
  const [selectedProveedores, setSelectedProveedores] = useState(
    grupo?.miembros?.map(m => m.id) || []
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const filteredProveedores = proveedores.filter(p =>
    p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.identificacion.includes(searchTerm)
  );

  const toggleProveedor = (id) => {
    setSelectedProveedores(prev =>
      prev.includes(id)
        ? prev.filter(pId => pId !== id)
        : [...prev, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    try {
      setLoading(true);

      if (grupo) {
        await gastosApi.updateGrupoProveedor(grupo.id, {
          ...formData,
          proveedorIds: selectedProveedores
        });
        toast.success('Grupo actualizado');
      } else {
        await gastosApi.createGrupoProveedor({
          ...formData,
          proveedorIds: selectedProveedores
        });
        toast.success('Grupo creado');
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving grupo:', error);
      toast.error(error.response?.data?.error || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const colorOptions = [
    '#EF4444', '#F97316', '#F59E0B', '#84CC16', '#22C55E',
    '#14B8A6', '#06B6D4', '#3B82F6', '#6366F1', '#8B5CF6',
    '#A855F7', '#EC4899', '#6B7280'
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto border-2 border-gray-200">
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white">
          <h3 className="text-lg font-semibold">
            {grupo ? 'Editar Grupo' : 'Nuevo Grupo de Proveedores'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre del Grupo *
            </label>
            <input
              type="text"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              placeholder="Ej: Tecnología, Servicios, Suministros..."
              className="w-full border-2 rounded-lg px-3 py-2 focus:border-primary-500 focus:ring-0"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <input
              type="text"
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              placeholder="Descripción del grupo (opcional)"
              className="w-full border-2 rounded-lg px-3 py-2 focus:border-primary-500 focus:ring-0"
            />
          </div>

          {/* Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Color
            </label>
            <div className="flex gap-2 flex-wrap">
              {colorOptions.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData({ ...formData, color })}
                  className={`w-8 h-8 rounded-full border-2 transition-transform ${
                    formData.color === color ? 'scale-125 border-gray-800' : 'border-transparent hover:scale-110'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          {/* Proveedores */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Proveedores en este grupo ({selectedProveedores.length})
            </label>

            <div className="border-2 rounded-lg overflow-hidden">
              {/* Search */}
              <div className="p-2 border-b bg-gray-50">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    placeholder="Buscar proveedor..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 border rounded text-sm"
                  />
                </div>
              </div>

              {/* Provider list */}
              <div className="max-h-60 overflow-y-auto">
                {filteredProveedores.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    No se encontraron proveedores
                  </div>
                ) : (
                  filteredProveedores.map((p) => (
                    <label
                      key={p.id}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedProveedores.includes(p.id)}
                        onChange={() => toggleProveedor(p.id)}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-gray-900 truncate">{p.nombre}</div>
                        <div className="text-xs text-gray-500">{p.identificacion}</div>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border-2 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border-2 border-green-500 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50"
            >
              <Save size={18} />
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
